// OSPROD-81: detect the local client (Windows App / macOS App / Web +
// browser) so we can show it on each participant's voice tile during
// a call. The result rides in the socket handshake `auth` payload so
// the server can echo it back on every `voice:state:changed` /
// `voice:participants` broadcast — late joiners then see existing
// participants' clients without any client-to-client handshake.
//
// Detection is best-effort UA parsing. Electron is identified by the
// preload-injected `window.electronAPI` (same signal as IS_ELECTRON
// in services/config.ts), and the browser-only path leans on a few
// vendor-specific UA tokens. Brave's `navigator.brave.isBrave()` is
// async — `detectClientInfoAsync` does that follow-up check and the
// socket layer re-emits the upgraded label if the answer comes back
// positive.

import { IS_ELECTRON, IS_CAPACITOR } from '../services/config'
import { APP_VERSION } from '../services/version'
import type { VoiceClientInfo } from '../types'

export type ClientPlatform =
  | 'windows-app'
  | 'macos-app'
  | 'linux-app'
  | 'desktop-app'
  | 'android-app'
  | 'ios-app'
  | 'web-windows'
  | 'web-macos'
  | 'web-linux'
  | 'web-android'
  | 'web-ios'
  | 'unknown'

export type WebBrowser =
  | 'chrome'
  | 'safari'
  | 'firefox'
  | 'opera'
  | 'edge'
  | 'brave'
  | 'other'

const UA = typeof navigator !== 'undefined' ? navigator.userAgent : ''
const PLATFORM_HINT = typeof navigator !== 'undefined' ? (navigator.platform || '') : ''

function detectOs(): 'windows' | 'macos' | 'linux' | 'android' | 'ios' | 'unknown' {
  // Order matters: iPadOS reports "Macintosh" in modern Safari, so we
  // check for the touch capability first to keep tablets out of the
  // macos bucket.
  if (/iPhone|iPad|iPod/i.test(UA)) return 'ios'
  if (typeof navigator !== 'undefined'
    && /Macintosh/i.test(UA)
    && (navigator as any).maxTouchPoints > 1) {
    return 'ios'
  }
  if (/Android/i.test(UA)) return 'android'
  if (/Windows/i.test(UA) || /Win/i.test(PLATFORM_HINT)) return 'windows'
  if (/Mac OS X|Macintosh/i.test(UA) || /Mac/i.test(PLATFORM_HINT)) return 'macos'
  if (/Linux/i.test(UA)) return 'linux'
  return 'unknown'
}

// Memoized "is this a phone/tablet web browser?" check. UA doesn't
// change within a session, so compute once. Electron desktop and
// desktop browsers return false. Used by the denoiser cascade to skip
// DeepFilterNet on mobile — its ONNX/WASM inference can't hold the
// 10ms/frame budget on phone CPUs and cascades to RNNoise mid-call
// (an audible ~2s glitch first). RNNoise is light and stable there.
let _isMobileClient: boolean | null = null
export function isMobileClient(): boolean {
  if (_isMobileClient === null) {
    const os = detectOs()
    _isMobileClient = os === 'android' || os === 'ios'
  }
  return _isMobileClient
}

function detectBrowser(): WebBrowser {
  // Order matters: Edge / Opera both ship "Chrome/" too, and Chrome
  // ships "Safari/" — the vendor-unique token is the only reliable
  // discriminator, so we test those first and fall back to the
  // engine names.
  if (/Edg\//i.test(UA)) return 'edge'
  if (/OPR\//i.test(UA) || /Opera\//i.test(UA)) return 'opera'
  if (/Firefox\//i.test(UA)) return 'firefox'
  if (/Chrome\//i.test(UA)) return 'chrome'
  if (/Safari\//i.test(UA)) return 'safari'
  return 'other'
}

function browserLabel(b: WebBrowser): string {
  switch (b) {
    case 'chrome': return 'Chrome'
    case 'safari': return 'Safari'
    case 'firefox': return 'Firefox'
    case 'opera': return 'Opera'
    case 'edge': return 'Edge'
    case 'brave': return 'Brave'
    case 'other': return 'Browser'
  }
}

// PWA = the page was installed and launched as a standalone app
// (Android "Add to Home screen", Chrome desktop install, iOS A2HS).
// OSPROD-81 explicitly asked us to distinguish these from a
// regular browser tab so support can tell at a glance whether the
// user is on the installed app surface (no URL bar, no tabs).
function isPWA(): boolean {
  if (typeof window === 'undefined') return false
  try {
    if (typeof window.matchMedia === 'function'
      && window.matchMedia('(display-mode: standalone)').matches) return true
  } catch { /* matchMedia not supported */ }
  // iOS Safari exposes the legacy `navigator.standalone` boolean
  // instead of supporting the display-mode media query.
  if (typeof navigator !== 'undefined' && (navigator as any).standalone === true) return true
  return false
}

// Common embedded WebViews bundled inside social apps. They carry
// their own UA tokens that the sync browser detector classifies as
// Chrome/Safari (because they ARE Chromium/WebKit under the hood),
// which is misleading — none of these surfaces support the same
// permission model, autoplay rules, or audio worklet capabilities
// as the real browser. Calling them out by name turns "audio doesn't
// work for one specific user" into a 5-second triage.
function detectWebView(): string | null {
  if (/Telegram/i.test(UA)) return 'Telegram'
  if (/\b(FBAN|FBAV|FB_IAB|FBIOS)\b/i.test(UA)) return 'Facebook'
  if (/Instagram/i.test(UA)) return 'Instagram'
  if (/\b(Twitter|TwitterAndroid)\b/i.test(UA)) return 'Twitter'
  if (/\bLine\//i.test(UA)) return 'Line'
  if (/MicroMessenger|WeChat/i.test(UA)) return 'WeChat'
  if (/TikTok|musical_ly/i.test(UA)) return 'TikTok'
  // Generic Android WebView marker — every system WebView UA carries
  // "; wv)" inside the OS block. Many in-app browsers fall back to
  // the system WebView when they don't ship their own Chromium fork
  // (Slack, Discord mobile preview, etc.), so this is the catch-all.
  if (/Android/i.test(UA) && /;\s*wv\s*\)/i.test(UA)) return 'WebView'
  return null
}

export function detectClientInfo(): VoiceClientInfo {
  const os = detectOs()
  const isMobile = os === 'android' || os === 'ios'
  if (IS_ELECTRON) {
    const platform: ClientPlatform = os === 'windows'
      ? 'windows-app'
      : os === 'macos'
        ? 'macos-app'
        : os === 'linux'
          ? 'linux-app'
          : 'desktop-app'
    const label = platform === 'windows-app'
      ? 'Windows App'
      : platform === 'macos-app'
        ? 'macOS App'
        : platform === 'linux-app'
          ? 'Linux App'
          : 'Desktop App'
    return { platform, label, isElectron: true, isMobile: false, appVersion: APP_VERSION }
  }
  // Capacitor mobile shell: a WebView of the web app, but it IS the
  // installed native app — surface it like the Electron desktop app
  // ("Android App" / "iOS App"), not the misleading "Android / WebView"
  // the system-WebView UA would otherwise yield.
  if (IS_CAPACITOR) {
    const platform: ClientPlatform = os === 'ios' ? 'ios-app' : 'android-app'
    const label = os === 'ios' ? 'iOS App' : os === 'android' ? 'Android App' : 'Mobile App'
    return { platform, label, isElectron: false, isMobile: true, appVersion: APP_VERSION }
  }
  const browser = detectBrowser()
  const platform: ClientPlatform = os === 'windows'
    ? 'web-windows'
    : os === 'macos'
      ? 'web-macos'
      : os === 'linux'
        ? 'web-linux'
        : os === 'android'
          ? 'web-android'
          : os === 'ios'
            ? 'web-ios'
            : 'unknown'
  const osPrefix = os === 'android'
    ? 'Android'
    : os === 'ios'
      ? 'iOS'
      : 'Web'
  // Embedded WebView takes priority over the browser label — the
  // engine underneath is Chrome/Safari but the user is NOT on the
  // real browser, and that distinction is what the tile is meant
  // to communicate. Falls through to standard browser label when
  // not in a known WebView, then appends a PWA marker when the
  // page was launched in standalone mode.
  const webView = detectWebView()
  let label = `${osPrefix} / ${webView ?? browserLabel(browser)}`
  if (!webView && isPWA()) label += ' (PWA)'
  return { platform, browser, label, isElectron: false, isMobile, appVersion: APP_VERSION }
}

// Native shell build number. Electron exposes it via the preload IPC
// `get-system-info` (app.getVersion()); Capacitor via @capacitor/app's
// App.getInfo() — which the wrapper may not have installed yet, so this
// returns undefined there and the badge simply omits the line. Best-effort:
// any failure resolves to undefined, never throws.
async function detectNativeBuild(): Promise<string | undefined> {
  try {
    if (IS_ELECTRON) {
      const api = (window as any)?.electronAPI
      if (api?.getSystemInfo) {
        const sys = await api.getSystemInfo()
        const v = sys?.version
        return typeof v === 'string' && v ? v : undefined
      }
    } else if (IS_CAPACITOR) {
      const App = (window as any)?.Capacitor?.Plugins?.App
      if (App?.getInfo) {
        const info = await App.getInfo()
        const v = info?.version
        if (typeof v === 'string' && v) {
          const b = info?.build
          return b ? `${v} (${b})` : v
        }
      }
    }
  } catch {
    // native build is optional metadata; swallow IPC/plugin errors
  }
  return undefined
}

// Two async upgrades over the sync detection: the Brave vendor check
// (its UA masquerades as Chrome) and the native shell build number. Both
// land a tick later and the socket layer re-emits voice:client-info.
export async function detectClientInfoAsync(): Promise<VoiceClientInfo> {
  let info = detectClientInfo()
  if (!info.isElectron && !IS_CAPACITOR && info.browser === 'chrome') {
    const brave = (navigator as any).brave
    if (brave?.isBrave) {
      try {
        if (await brave.isBrave()) info = { ...info, browser: 'brave', label: 'Web / Brave' }
      } catch {
        // Some Brave builds gate isBrave() behind a permission prompt and
        // throw if denied. Falling back to "Chrome" is fine — Brave
        // intentionally masquerades as Chrome when sites ask.
      }
    }
  }
  const nativeBuild = await detectNativeBuild()
  if (nativeBuild) info = { ...info, nativeBuild }
  return info
}
